export type PwaState = {
  supported: boolean;
  offline: boolean;
  updateReady: boolean;
  installReady: boolean;
};
export type InstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};
export function registerPwaLifecycle(listener: (state: PwaState) => void) {
  let registration: ServiceWorkerRegistration | null = null;
  let installPrompt: InstallPromptEvent | null = null;
  let idleHandle: number | null = null;
  let loadListener: (() => void) | null = null;
  let state: PwaState = {
    supported: 'serviceWorker' in navigator,
    offline: !navigator.onLine,
    updateReady: false,
    installReady: false,
  };
  const emit = (patch: Partial<PwaState>) => {
    state = { ...state, ...patch };
    listener(state);
  };
  const online = () => emit({ offline: false });
  const offline = () => emit({ offline: true });
  const install = (event: Event) => {
    event.preventDefault();
    installPrompt = event as InstallPromptEvent;
    emit({ installReady: true });
  };
  const installed = () => {
    installPrompt = null;
    emit({ installReady: false });
  };
  const simulatedUpdate = () => emit({ updateReady: true });
  let updateCheck: Promise<void> | null = null;
  const checkUpdate = () => {
    if (!registration || !navigator.onLine || updateCheck) return;
    const current = registration;
    updateCheck = current.update().then(() => {
      if (current.waiting) emit({ updateReady: true });
    }).catch(() => {
      // Keep the current screen and its inputs; the existing update action remains available.
    }).finally(() => { updateCheck = null; });
  };
  const restored = (event: PageTransitionEvent) => { if (event.persisted) checkUpdate(); };
  window.addEventListener('online', online);
  window.addEventListener('offline', offline);
  window.addEventListener('beforeinstallprompt', install);
  window.addEventListener('appinstalled', installed);
  window.addEventListener('kop-pwa-update-ready', simulatedUpdate);
  window.addEventListener('kop-pwa-check-update', checkUpdate);
  window.addEventListener('pageshow', restored);
  emit({});
  // The generated worker exists only in a production build/preview. Registering
  // it against Vite's development fallback returns index.html as JavaScript and
  // creates noisy MIME errors in local development and browser tests.
  const register = () => {
    idleHandle = null;
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((value) => {
        registration = value;
        if (value.waiting) emit({ updateReady: true });
        value.addEventListener('updatefound', () =>
          value.installing?.addEventListener('statechange', () => {
            if (value.waiting && navigator.serviceWorker.controller) emit({ updateReady: true });
          }),
        );
        return value.update();
      })
      .catch(() => emit({ supported: false }));
  };
  const scheduleRegistration = () => {
    loadListener = null;
    const idleWindow = window as Window & {
      requestIdleCallback?: (work: () => void, options?: { timeout: number }) => number;
    };
    idleHandle = idleWindow.requestIdleCallback
      ? idleWindow.requestIdleCallback(register, { timeout: 3_000 })
      : window.setTimeout(register, 1_000);
  };
  if (state.supported && import.meta.env.PROD) {
    if (document.readyState === 'complete') scheduleRegistration();
    else {
      loadListener = scheduleRegistration;
      window.addEventListener('load', loadListener, { once: true });
    }
  }
  let reloading = false;
  const controlledAtStart = Boolean(navigator.serviceWorker?.controller);
  const changed = () => {
    if (controlledAtStart && !reloading) {
      reloading = true;
      window.location.reload();
    }
  };
  navigator.serviceWorker?.addEventListener('controllerchange', changed);
  return {
    get state() {
      return state;
    },
    async install() {
      if (!installPrompt) return;
      await installPrompt.prompt();
      await installPrompt.userChoice;
      installPrompt = null;
      emit({ installReady: false });
    },
    async applyUpdate() {
      const activeRegistration =
        registration ?? (await navigator.serviceWorker?.getRegistration('/')) ?? null;
      if (!activeRegistration) {
        emit({ updateReady: false });
        window.location.reload();
        return;
      }
      registration = activeRegistration;
      if (!activeRegistration.waiting) {
        try {
          await activeRegistration.update();
        } catch {
          // A network failure must not leave a permanently inert update action.
          // Navigation requests are network-first in the generated worker, so a
          // reload remains the safe recovery path.
        }
      }
      const waiting = activeRegistration.waiting;
      if (waiting) waiting.postMessage({ type: 'SKIP_WAITING' });
      emit({ updateReady: false });
      // controllerchange normally reloads after SKIP_WAITING. The timeout also
      // handles a worker that activated between rendering and the user's click.
      window.setTimeout(() => {
        if (!reloading) {
          reloading = true;
          window.location.reload();
        }
      }, waiting ? 1_200 : 0);
    },
    dispose() {
      if (loadListener) window.removeEventListener('load', loadListener);
      if (idleHandle !== null) {
        const idleWindow = window as Window & { cancelIdleCallback?: (handle: number) => void };
        if (idleWindow.cancelIdleCallback) idleWindow.cancelIdleCallback(idleHandle);
        else window.clearTimeout(idleHandle);
      }
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      window.removeEventListener('beforeinstallprompt', install);
      window.removeEventListener('appinstalled', installed);
      window.removeEventListener('kop-pwa-update-ready', simulatedUpdate);
      window.removeEventListener('kop-pwa-check-update', checkUpdate);
      window.removeEventListener('pageshow', restored);
      navigator.serviceWorker?.removeEventListener('controllerchange', changed);
    },
  };
}
